import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import FaqSection from "@/components/common/FaqSection";
import HeroVideo from "@/components/common/HeroVideo";
import FeaturedProjects from "@/components/home/Industries";
import RelatedCaseStudies from "@/components/common/RelatedCaseStudies";
import ProofStrip from "@/components/common/ProofStrip";
import CommercialCTA from "@/components/common/CommercialCTA";
import { SERVICE_FAQS } from "@/data/faqs";
import { trackViewOnce, trackClick } from "@/lib/analytics";
import { openEstimateWizard } from "@/lib/cta";
import api from "@/lib/api";

/**
 * ServicePage — premium, animated service landing template.
 *
 * Uses the CloudLab core brand (primary / secondary tokens) so every service
 * page feels part of the same family. Content comes from `serviceConfigs.js`.
 *
 * Phase 3.3: primary CTA opens the central ProjectEstimateWizard with
 * full service context (serviceSlug → project_type preselect + analytics
 * source parameters). ProofStrip renders contextual verified evidence
 * AFTER capabilities. Both remain hidden if there is no approved data.
 */
export default function ServicePage({ config: c, marketSlug = null, proofMetricIds = null }) {
  const openEstimate = (ctaLocation = "hero") => {
    trackClick("cta_clicked", {
      cta_type: "get_project_estimate",
      source_type: "service_page",
      cta_location: ctaLocation,
      service_slug: c.slug,
      market_slug: marketSlug || undefined,
    });
    openEstimateWizard({
      serviceSlug: c.slug,
      serviceName: c.name,
      marketSlug: marketSlug || null,
      sourceType: "service_page",
      ctaLocation,
    });
  };
  const [videoUrl, setVideoUrl] = useState("");

  // GA4/GTM: service_page_view (deduped per page-load)
  useEffect(() => {
    trackViewOnce("service_page_view", {
      service_slug: c.slug,
      service_name: c.name,
      market: marketSlug || "global",
    });
  }, [c.slug, c.name, marketSlug]);

  useEffect(() => {
    let mounted = true;
    api.get(`/services/media/${c.slug}`)
      .then((r) => { if (mounted) setVideoUrl(r.data?.video_url || ""); })
      .catch(() => { if (mounted) setVideoUrl(""); });
    return () => { mounted = false; };
  }, [c.slug]);

  const primaryLabel = c.ctas?.primary?.label || "Start a project";
  return (
    <div className="relative" data-testid={`service-page-${c.slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 15% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%), radial-gradient(900px 400px at 100% 20%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-20 pb-10 md:pb-14">
          <Link
            to="/services"
            className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors"
          >
            ← All services
          </Link>

          <div className="mt-5 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">{c.kicker}</span>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <h1
                className="font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-7xl"
                data-testid={`${c.slug}-hero-title`}
              >
                {c.hero.title}{" "}
                <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
                  {c.hero.highlight}
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
                {c.hero.subtitle}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => openEstimate("hero")}
                  data-testid={`${c.slug}-hero-cta-primary`}
                  className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
                >
                  <span>{primaryLabel}</span>
                  <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
              </div>

              {c.hero.stats?.length > 0 && (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
                  {c.hero.stats.map((s, i) => (
                    <motion.div
                      key={s.l}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
                    >
                      <div className="font-heading font-black text-3xl md:text-4xl leading-none text-primary">
                        {s.v}
                      </div>
                      <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground leading-snug">
                        {s.l}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Hero visual — portrait 9/16 to feel like a phone-recorded reel */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-[300px] lg:max-w-[340px] rounded-[32px] overflow-hidden aspect-[9/16] border border-border shadow-[0_35px_80px_-25px_rgba(0,0,0,0.4)]">
                {videoUrl ? (
                  <HeroVideo src={videoUrl} testid={`${c.slug}-hero-video`} />
                ) : (
                  <img src={c.hero_image} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                )}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.55) 80%, rgba(15,23,42,0.9) 100%)",
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 pointer-events-none">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary-foreground/85">
                    Service · CloudLab Technologies
                  </div>
                  <div className="mt-1 font-heading font-bold text-white text-xl leading-tight">
                    {c.name}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      {c.capabilities && (
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-6">
            <SectionHeader
              kicker={c.capabilities.kicker}
              title={c.capabilities.title}
              subtitle={c.capabilities.subtitle}
            />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {c.capabilities.items.map((f, i) => {
                const Icon = Icons[f.icon] || Icons.Sparkles;
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                    className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/40 transition-all hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.25)]"
                    data-testid={`${c.slug}-cap-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-primary/10 text-primary transition-transform group-hover:scale-105">
                      <Icon className="w-5 h-5" strokeWidth={1.6} />
                    </div>
                    <div className="mt-4 font-heading font-bold text-lg leading-tight">{f.title}</div>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROOF STRIP — verified evidence from projectMetrics.js.
          Rendered ONLY when approved+relevant metrics exist for this service.
          Metrics are filtered per service via `proofMetricIds` (passed by
          ServiceDetail.jsx) so each service surfaces a bespoke, non-generic set. */}
      <ProofStrip serviceSlug={c.slug} metricIds={proofMetricIds} />

      {/* TECH / TOOLS */}
      {c.tech?.items?.length > 0 && (
        <section className="py-10 md:py-14 bg-muted/40 border-y border-border/60">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">{c.tech.kicker}</div>
              {c.tech.title && (
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight">
                  {c.tech.title}
                </h2>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {c.tech.items.map((t) => (
                <span
                  key={t.name}
                  className="px-4 py-2 rounded-full bg-background border border-border text-sm font-medium hover:border-primary/50 hover:text-primary transition-colors"
                >
                  {t.name}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HOW WE WORK */}
      {c.steps && (
        <section className="py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHeader kicker={c.steps.kicker} title={c.steps.title} />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {c.steps.items.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-heading font-black text-lg bg-primary text-primary-foreground">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-4 font-heading font-bold text-lg leading-tight">{s.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OUTCOMES */}
      {c.outcomes?.length > 0 && (
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Outcomes</div>
              <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-tight">
                What you actually get.
              </h2>
              <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">
                Every {c.name} engagement ends with hard business outcomes — not slideware.
                Here&apos;s what a typical client walks away with.
              </p>
              <button onClick={() => openEstimate("outcomes")} className="group mt-6 inline-flex rounded-full pl-6 pr-2 py-2 items-center gap-3 text-sm font-medium bg-primary text-primary-foreground" data-testid={`${c.slug}-outcomes-cta`}>
                <span>{primaryLabel}</span>
                <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
            </div>
            <ul className="space-y-3">
              {c.outcomes.map((o) => (
                <li
                  key={o}
                  className="flex items-start gap-3 p-4 rounded-2xl bg-card border border-border"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      <FaqSection items={c.__faqs || SERVICE_FAQS[c.slug] || []} testidRoot={`${c.slug}-faq`} />

      {/* Related case studies — automatically filtered by this service slug
          (or by market slug when rendered inside a UKServicePage). Hidden if none tagged. */}
      <RelatedCaseStudies
        service={c.slug}
        market={marketSlug || undefined}
        kicker="Related work"
        title={`Verified ${c.name.toLowerCase()} projects`}
      />

      {/* Featured projects — same scrolling gallery as the homepage */}
      <FeaturedProjects />

      {/* International delivery — compact contextual market links.
          NOT hreflang alternates; these are normal crawlable navigational
          links from the service page to genuine market landing pages. */}
      <section className="py-14 md:py-20" data-testid={`${c.slug}-international`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-3xl border border-border bg-card p-7 md:p-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary">International delivery</div>
                <h2 className="mt-2 font-heading font-bold text-2xl md:text-3xl leading-tight">
                  {c.name} across our international markets.
                </h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                  CloudLab delivers {c.name.toLowerCase()} to businesses in these markets — pick the one closest to yours to see local delivery detail, invoicing and case work.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {[
                { p: "/uk",     label: "United Kingdom",       flag: "🇬🇧" },
                { p: "/us",     label: "United States",        flag: "🇺🇸" },
                { p: "/pk",     label: "Pakistan",             flag: "🇵🇰" },
                { p: "/ae",     label: "United Arab Emirates", flag: "🇦🇪" },
                { p: "/sa",     label: "Saudi Arabia",         flag: "🇸🇦" },
                { p: "/de",     label: "Germany",              flag: "🇩🇪" },
                { p: "/europe", label: "Europe",               flag: "🇪🇺" },
              ].map((m) => (
                <Link
                  key={m.p}
                  to={m.p}
                  className="rounded-full px-4 py-2 border border-border hover:border-primary/50 hover:bg-primary/5 text-sm inline-flex items-center gap-2 transition-colors"
                  data-testid={`${c.slug}-market-link-${m.p.replace(/^\//, "")}`}
                >
                  <span aria-hidden="true">{m.flag}</span>
                  <span className="font-medium">{m.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 bg-foreground text-background shadow-2xl">
            <div
              aria-hidden="true"
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary"
            />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Ready to start?</div>
                <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
                  Let&apos;s build your next {c.name.toLowerCase()} project.
                </h2>
                <p className="mt-4 text-background/80 leading-relaxed max-w-lg">
                  Share a rough spec or just a rough idea — we&apos;ll come back within one business day
                  with a realistic scope, timeline and price.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <CommercialCTA
                  variant="estimate-plus-consultation"
                  serviceSlug={c.slug}
                  serviceName={c.name}
                  marketSlug={marketSlug || null}
                  sourceType="service_page"
                  ctaLocation="final"
                  tone="on-dark"
                  testid={`${c.slug}-final-cta`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div className="max-w-3xl">
      {kicker && (
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
      )}
      <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
        {title}
      </h2>
      {subtitle && <p className="mt-3 text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
    </div>
  );
}

